
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useProjectSearch, type ProjectSearchFilter, type ProjectSearchResult } from '@/hooks/useProjectSearch'

interface ProjectSearchBarProps {
  className?: string
}

export const ProjectSearchBar: React.FC<ProjectSearchBarProps> = ({ className }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [activeFilter, setActiveFilter] = useState<ProjectSearchFilter>('all')
  const [recentSearches, setRecentSearches] = useState<string[]>([])

  const searchRef = useRef<HTMLDivElement>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { searchResults, isSearching, error, searchProjects, clearResults } = useProjectSearch()

  // Click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false)
      }
    }

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }

    return undefined
  }, [showSearchResults])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        const searchInput = document.querySelector(
          'input[placeholder*="Search projects"]'
        ) as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
          setShowSearchResults(true)
        }
      }

      // Escape to close search results
      if (event.key === 'Escape' && showSearchResults) {
        setShowSearchResults(false)
        const searchInput = document.querySelector(
          'input[placeholder*="Search projects"]'
        ) as HTMLInputElement
        if (searchInput) {
          searchInput.blur()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showSearchResults])

  // Search functionality
  const performSearch = async (query: string, filter?: ProjectSearchFilter) => {
    const finalFilter = filter || activeFilter
    await searchProjects(query, finalFilter)
    setShowSearchResults(true)
  }

  // Handle search input with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set new timeout for debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      if (query.trim()) {
        performSearch(query)
      } else {
        // Still show all projects on empty search
        performSearch('')
      }
    }, 300)

    setShowSearchResults(true)
  }

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim() && !recentSearches.includes(searchQuery)) {
      setRecentSearches((prev) => [searchQuery, ...prev.slice(0, 4)])
    }
    performSearch(searchQuery)
  }

  // Clear search
  const clearSearch = () => {
    setSearchQuery('')
    clearResults()
    setShowSearchResults(false)
  }

  // Handle filter change
  const handleFilterChange = (filter: ProjectSearchFilter) => {
    setActiveFilter(filter)
    // Always search when filter changes, even with empty query
    performSearch(searchQuery, filter)
  }

  // Handle recent search selection
  const handleRecentSearchSelect = (search: string) => {
    setSearchQuery(search)
    performSearch(search)
  }

  // Handle result click (project or canvas)
  const handleResultClick = (result: ProjectSearchResult) => {
    if (result.type === 'project') {
      navigate({ to: `/workspace/project/${result.id}` })
    } else if (result.type === 'canvas') {
      navigate({ to: `/canvas/${result.id}` })
    }
    setShowSearchResults(false)
  }

  return (
    <div className={`relative w-full ${className}`} ref={searchRef}>
      <form onSubmit={handleSearchSubmit}>
        <div className="relative">
          <Input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => {
              setShowSearchResults(true)
              if (searchResults.length === 0) {
                // Load projects based on current filter on focus
                performSearch(searchQuery)
              }
            }}
            placeholder="Search projects... (⌘K)"
            inputSize="md"
            className="w-full pl-10 pr-10"
          />
          <Icon
            name="search"
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          {searchQuery && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 transform p-0"
              onClick={clearSearch}
            >
              <Icon name="x" className="h-3 w-3" />
            </Button>
          )}
        </div>
      </form>

      {/* Search Results Dropdown */}
      {showSearchResults && (
        <div className="bg-popover border-border absolute top-full right-0 left-0 z-50 mt-2 max-h-96 overflow-hidden rounded-lg border shadow-xl">
          {/* Filter Tabs */}
          <div className="border-b border-border p-3">
            <div className="flex gap-1">
              {(['all', 'recent', 'drafts', 'canvas', 'files'] as const).map((filter) => (
                <Button
                  key={filter}
                  variant={activeFilter === filter ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleFilterChange(filter)}
                  className="h-6 text-xs capitalize"
                >
                  <Icon
                    name={
                      filter === 'all' ? 'folder' :
                      filter === 'recent' ? 'clock' :
                      filter === 'drafts' ? 'file-text' :
                      filter === 'canvas' ? 'image' :
                      'folder-open'
                    }
                    className="h-3 w-3 mr-1"
                  />
                  {filter === 'all' ? 'All' :
                   filter === 'recent' ? 'Recent' :
                   filter === 'drafts' ? 'Drafts' :
                   filter === 'canvas' ? 'Sessions' : 'Files'}
                </Button>
              ))}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
              </div>
            ) : activeFilter === 'files' ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Icon name="folder-open" className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Files feature coming soon</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="p-2">
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <div className="h-8 w-12 rounded border bg-muted flex items-center justify-center">
                      {result.thumbnail ? (
                        <img
                          src={result.thumbnail}
                          alt={result.name}
                          className="h-full w-full rounded object-cover"
                        />
                      ) : (
                        <Icon
                          name={result.type === 'project' ? 'folder' : 'file'}
                          className="h-4 w-4 text-muted-foreground"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{result.name}</p>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          result.type === 'project'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        }`}>
                          {result.type}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {result.type === 'project'
                          ? `${result.canvasCount} ${result.canvasCount === 1 ? 'canvas' : 'canvases'}`
                          : result.project ? `in ${result.project.name}` : 'Draft'
                        } •
                        {' '}{new Date(result.lastModified).toLocaleDateString()}
                      </p>
                    </div>
                    <Icon name="arrow-right" className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Icon name="alert-circle" className="h-8 w-8 text-red-500 mb-2" />
                <p className="text-sm text-red-600">{error}</p>
                <p className="text-xs text-muted-foreground mt-1">Check if API server is running</p>
              </div>
            ) : searchQuery ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Icon name="search" className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No projects found for &quot;{searchQuery}&quot;</p>
              </div>
            ) : recentSearches.length > 0 ? (
              <div className="p-2">
                <p className="text-xs font-medium text-muted-foreground px-3 py-2">Recent Searches</p>
                {recentSearches.map((search, index) => (
                  <div
                    key={index}
                    onClick={() => handleRecentSearchSelect(search)}
                    className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Icon name="clock" className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{search}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <Icon name="folder" className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No projects found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
